// Point d’entrée. Choix du stockage : Supabase en production ; mémoire uniquement en développement explicite.
import '@fontsource-variable/manrope';
import '@fontsource-variable/dm-sans';
import './styles/app.css';
import { MemoryAuth, SupabaseAuth } from './data/auth';
import { LocalCache } from './data/cache';
import { setGoogleTokenProvider } from './data/google';
import { setLogeTokenProvider } from './data/loge';
import { MemoryRepo } from './data/memoryRepo';
import { setPushTokenProvider } from './data/push';
import { setRdvTokenProvider } from './data/rdv';
import { Store } from './data/store';
import { makeSupabaseClient, SupabaseRepo } from './data/supabaseRepo';
import { mountApp } from './ui/app';
import { versionLabel } from './core/version';

const root = document.getElementById('app')!;
const memoryMode = import.meta.env.DEV && import.meta.env.VITE_BACKEND === 'memory';

let store: Store;
try {
  if (memoryMode) {
    // Développement seulement : `?now=2026-10-25T07:00:00Z` décale l’horloge pour observer un autre instant
    // (changement d’heure, fenêtre commune ouverte…). L’horloge continue d’avancer à partir de là.
    const fakeNow = Date.parse(new URLSearchParams(location.search).get('now') ?? '');
    if (Number.isFinite(fakeNow)) {
      const realNow = Date.now.bind(Date);
      const offset = fakeNow - realNow();
      Date.now = () => realNow() + offset;
    }
    store = new Store(new MemoryRepo(), new MemoryAuth(), { dev: true });
  } else {
    const client = makeSupabaseClient();
    const repo = new SupabaseRepo(client, () => store?.state.user?.id ?? null);
    store = new Store(repo, new SupabaseAuth(client), { cache: new LocalCache() });
    const sessionToken = async () => (await client.auth.getSession()).data.session?.access_token ?? null;
    setGoogleTokenProvider(sessionToken);
    setPushTokenProvider(sessionToken);
    setRdvTokenProvider(sessionToken);
    setLogeTokenProvider(sessionToken);
  }
} catch (e) {
  root.innerHTML = `<div class="splash"><span class="splash-mark" aria-hidden="true"><img src="/icons/monogram-96.png" alt=""></span><p><b>Configuration manquante.</b><br>${e instanceof Error ? e.message : String(e)}</p><p class="hint">${versionLabel()}</p></div>`;
  throw e;
}

mountApp(root, store);
void store.start();

// Service worker de l’application installable : mise à jour automatique, jamais de données en cache.
// Le navigateur ne revérifie le service worker que sur un vrai rechargement de page ; une PWA rouverte
// depuis l’arrière-plan (mobile) ne recharge pas forcément. On force donc la vérification à chaque retour
// au premier plan, en plus du contrôle automatique du navigateur au chargement.
if (!memoryMode && 'serviceWorker' in navigator) {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisterError: (e) => console.warn('Service worker :', e),
      onRegisteredSW: (_url, registration) => {
        if (!registration) return;
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void registration.update();
        });
      },
    });
  });
}
